/**
 * /my/team — Team Dashboard (Section 3A).
 *
 * Managers land here to see their direct reports at a glance: alerts,
 * a roster grid with PTO / docs / review state, and a monthly calendar
 * that overlays approved leave + company holidays.
 *
 * Access is gated at the router layer (MANAGER / HR_ADMIN / SUPER_ADMIN)
 * and again at the backend via ``require_roles`` — so this page simply
 * renders whatever the API gives it.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Calendar,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileWarning,
  Info,
  Users,
} from "lucide-react";
import { fullName, pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Tabs, TabPanel } from "@/components/ui/Tabs";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/hooks/useToast";
import {
  getMyTeam,
  getMyTeamAlerts,
  getMyTeamCalendar,
  type TeamAlert,
  type TeamCalendarEntry,
  type TeamMember,
  type TeamRoster,
} from "@/api/team";

type SortKey = "name" | "hire_date" | "pto";

function formatMonths(n: number): string {
  if (n < 1) return "< 1 mo";
  if (n < 12) return `${n} mo`;
  const years = Math.floor(n / 12);
  const months = n % 12;
  return months ? `${years}y ${months}m` : `${years}y`;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function monthBounds(year: number, month: number): [Date, Date] {
  const start = new Date(Date.UTC(year, month, 1));
  const end = new Date(Date.UTC(year, month + 1, 0));
  return [start, end];
}

function leaveColor(leaveType: string | null | undefined): string {
  // Leave-type → tailwind color token. Keeps the spec's red/blue/green palette
  // readable in both light and dark themes via the accent/success/danger tokens.
  switch ((leaveType || "").toUpperCase()) {
    case "SICK":
      return "bg-danger/80 text-white";
    case "PERSONAL":
    case "VOLUNTEER":
    case "CAREGIVING":
      return "bg-success/80 text-white";
    case "PTO":
    case "VACATION":
    default:
      return "bg-accent/80 text-white";
  }
}

function alertIcon(severity: string) {
  if (severity === "danger") return <AlertCircle className="h-5 w-5 text-danger" />;
  if (severity === "warning")
    return <AlertTriangle className="h-5 w-5 text-warning" />;
  return <Info className="h-5 w-5 text-accent" />;
}

export function TeamDashboardPage() {
  const { addToast } = useToast();

  const [roster, setRoster] = useState<TeamRoster | null>(null);
  const [alerts, setAlerts] = useState<TeamAlert[]>([]);
  const [calendar, setCalendar] = useState<TeamCalendarEntry[]>([]);
  const [loadingRoster, setLoadingRoster] = useState(true);
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  const [tab, setTab] = useState<"roster" | "calendar">("roster");
  const [sort, setSort] = useState<SortKey>("name");

  const today = useMemo(() => new Date(), []);
  const [calMonth, setCalMonth] = useState<{ year: number; month: number }>({
    year: today.getUTCFullYear(),
    month: today.getUTCMonth(),
  });

  // ── Data loaders ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingRoster(true);
      try {
        const [r, a] = await Promise.all([getMyTeam(), getMyTeamAlerts()]);
        if (cancelled) return;
        setRoster(r);
        setAlerts(a.alerts);
      } catch (err) {
        if (!cancelled) {
          addToast("error", "Couldn't load team dashboard");
        }
      } finally {
        if (!cancelled) setLoadingRoster(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [addToast]);

  useEffect(() => {
    if (tab !== "calendar") return;
    let cancelled = false;
    async function load() {
      setLoadingCalendar(true);
      const [start, end] = monthBounds(calMonth.year, calMonth.month);
      try {
        const rows = await getMyTeamCalendar(isoDate(start), isoDate(end));
        if (!cancelled) setCalendar(rows);
      } catch {
        if (!cancelled) addToast("error", "Couldn't load team calendar");
      } finally {
        if (!cancelled) setLoadingCalendar(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [tab, calMonth, addToast]);

  // ── Sorted members ──────────────────────────────────────────────
  const sortedMembers = useMemo(() => {
    if (!roster) return [];
    const arr = [...roster.members];
    switch (sort) {
      case "hire_date":
        arr.sort((a, b) => a.hire_date.localeCompare(b.hire_date));
        break;
      case "pto":
        arr.sort((a, b) => (b.pto_balance ?? -Infinity) - (a.pto_balance ?? -Infinity));
        break;
      default:
        arr.sort(
          (a, b) =>
            a.last_name.localeCompare(b.last_name) ||
            a.first_name.localeCompare(b.first_name),
        );
    }
    return arr;
  }, [roster, sort]);

  // ── Calendar grid helpers ───────────────────────────────────────
  const calByDate = useMemo(() => {
    const m = new Map<string, TeamCalendarEntry[]>();
    for (const entry of calendar) {
      const key = entry.date;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(entry);
    }
    return m;
  }, [calendar]);

  const calendarCells = useMemo(() => {
    const [first, last] = monthBounds(calMonth.year, calMonth.month);
    const startDayOfWeek = first.getUTCDay(); // 0 = Sun
    const daysInMonth = last.getUTCDate();
    const cells: { date: Date | null }[] = [];
    for (let i = 0; i < startDayOfWeek; i++) cells.push({ date: null });
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({
        date: new Date(Date.UTC(calMonth.year, calMonth.month, d)),
      });
    }
    while (cells.length % 7 !== 0) cells.push({ date: null });
    return cells;
  }, [calMonth]);

  // ── Render ──────────────────────────────────────────────────────
  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="My Team"
        subtitle={
          roster
            ? `${roster.headcount} direct report${roster.headcount === 1 ? "" : "s"}${
                roster.on_leave_today > 0
                  ? ` · ${roster.on_leave_today} out today`
                  : ""
              }`
            : "Loading your team…"
        }
      />

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card className="mb-6">
          <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> Alerts
          </h3>
          <ul className="space-y-2">
            {alerts.map((a) => (
              <li
                key={a.code}
                className="flex items-center gap-3 p-3 rounded-lg bg-background border border-border"
              >
                {alertIcon(a.severity)}
                <span className="flex-1 text-sm text-text-primary">
                  {a.message}
                </span>
                {a.action_path && (
                  <Link
                    to={a.action_path}
                    className="text-sm text-accent hover:underline inline-flex items-center gap-1"
                  >
                    Review <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Tabs
        tabs={[
          { id: "roster", label: "Roster" },
          { id: "calendar", label: "Calendar" },
        ]}
        activeTab={tab}
        onChange={(id) => setTab(id as "roster" | "calendar")}
        aria-label="Team views"
      />

      {/* Roster tab */}
      <TabPanel id="roster" activeTab={tab}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-text-muted">
            {loadingRoster
              ? "Loading…"
              : `${sortedMembers.length} team member${sortedMembers.length === 1 ? "" : "s"}`}
          </p>
          <div className="flex items-center gap-2">
            <label className="text-sm text-text-muted">Sort by</label>
            <Select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              className="w-44"
              options={[
                { value: "name", label: "Name" },
                { value: "hire_date", label: "Hire date" },
                { value: "pto", label: "PTO balance" },
              ]}
            />
          </div>
        </div>

        {!loadingRoster && sortedMembers.length === 0 && (
          <Card className="text-center py-12">
            <Users className="h-8 w-8 text-text-muted mx-auto mb-2" />
            <p className="text-text-primary font-medium">No direct reports</p>
            <p className="text-sm text-text-muted mt-1">
              When employees are assigned to you as their manager, they&rsquo;ll
              show up here.
            </p>
          </Card>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedMembers.map((m) => (
            <TeamCard key={m.id} member={m} />
          ))}
        </div>
      </TabPanel>

      {/* Calendar tab */}
      <TabPanel id="calendar" activeTab={tab}>
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              {new Date(
                Date.UTC(calMonth.year, calMonth.month, 1),
              ).toLocaleString("default", { month: "long", year: "numeric" })}
            </h3>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() =>
                  setCalMonth((m) => {
                    const d = new Date(Date.UTC(m.year, m.month - 1, 1));
                    return {
                      year: d.getUTCFullYear(),
                      month: d.getUTCMonth(),
                    };
                  })
                }
                aria-label="Previous month"
                className="p-1.5 rounded hover:bg-background"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  const now = new Date();
                  setCalMonth({
                    year: now.getUTCFullYear(),
                    month: now.getUTCMonth(),
                  });
                }}
                className="text-xs px-2 py-1 rounded hover:bg-background text-text-muted"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() =>
                  setCalMonth((m) => {
                    const d = new Date(Date.UTC(m.year, m.month + 1, 1));
                    return {
                      year: d.getUTCFullYear(),
                      month: d.getUTCMonth(),
                    };
                  })
                }
                aria-label="Next month"
                className="p-1.5 rounded hover:bg-background"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden text-xs">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div
                key={d}
                className="bg-surface px-2 py-1.5 text-center text-text-muted font-medium"
              >
                {d}
              </div>
            ))}
            {calendarCells.map((cell, i) => {
              const iso = cell.date ? isoDate(cell.date) : null;
              const entries = iso ? calByDate.get(iso) || [] : [];
              return (
                <div
                  key={i}
                  className="bg-surface min-h-[96px] p-1.5 flex flex-col gap-1"
                >
                  {cell.date && (
                    <span className="text-[11px] text-text-muted">
                      {cell.date.getUTCDate()}
                    </span>
                  )}
                  {entries.slice(0, 4).map((e, idx) => (
                    <span
                      key={idx}
                      title={
                        e.type === "holiday"
                          ? `Holiday: ${e.employee_name}`
                          : `${e.employee_name} — ${e.leave_type || "Leave"}`
                      }
                      className={
                        e.type === "holiday"
                          ? "px-1.5 py-0.5 rounded bg-text-muted/10 text-text-primary truncate"
                          : `px-1.5 py-0.5 rounded truncate ${leaveColor(e.leave_type)}`
                      }
                    >
                      {e.type === "holiday" ? "🎉" : ""} {e.employee_name}
                    </span>
                  ))}
                  {entries.length > 4 && (
                    <span className="text-[10px] text-text-muted">
                      +{entries.length - 4} more
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {loadingCalendar && (
            <p className="text-xs text-text-muted mt-3">Loading calendar…</p>
          )}
        </Card>
      </TabPanel>
    </motion.div>
  );
}

function TeamCard({ member }: { member: TeamMember }) {
  const onLeaveNow = Boolean(member.current_leave);
  // Use the dedup-safe ``fullName`` helper. It defends against
  // all three known data shapes that produce "Laura Arce Arce":
  //   1. first_name="Laura Arce", last_name="Arce"
  //   2. last_name="Arce Arce" (doubled-word)
  //   3. preferred_name="Laura Arce", last_name="Arce"
  // See ``frontend/src/lib/utils.ts`` for the algorithm.
  let displayName = fullName(
    member.first_name,
    member.last_name,
    member.preferred_name,
  );
  // Defensive inline post-process. The bug that produced
  // "Laura Arce Arce" originally lived in this very file (commit
  // 7ece9e5 had ``{displayName} {member.last_name}`` which
  // double-appended the last name). Cached-bundle leaks of that
  // shape kept the bug visible after every fix. Even if some
  // future regression reintroduces a doubled trailing token,
  // this single-pass walk catches it before the JSX renders.
  if (displayName) {
    const _toks = displayName.split(/\s+/).filter(Boolean);
    const _kept: string[] = [];
    for (const _t of _toks) {
      if (
        _kept.length > 0 &&
        _kept[_kept.length - 1].toLowerCase() === _t.toLowerCase()
      ) {
        continue;
      }
      _kept.push(_t);
    }
    displayName = _kept.join(" ");
  }
  const pendingDocs = member.pending_documents;

  return (
    <Card
      className={
        onLeaveNow ? "opacity-90 ring-1 ring-accent/40" : undefined
      }
    >
      <div className="flex items-start gap-3">
        <Avatar
          firstName={member.first_name}
          lastName={member.last_name}
          src={member.avatar_url}
          size="md"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/employees/${member.id}`}
              className="font-semibold text-text-primary hover:underline truncate"
            >
              {displayName}
            </Link>
            {onLeaveNow && (
              <Badge variant="info">
                On {member.current_leave?.leave_type.toLowerCase()}
              </Badge>
            )}
          </div>
          <p className="text-xs text-text-muted truncate">{member.title}</p>
          {member.department_name && (
            <p className="text-xs text-text-muted truncate">
              {member.department_name}
            </p>
          )}
        </div>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
        <div>
          <dt className="text-text-muted">Tenure</dt>
          <dd className="text-text-primary font-medium">
            {formatMonths(member.tenure_months)}
          </dd>
        </div>
        <div>
          <dt className="text-text-muted">PTO balance</dt>
          <dd
            className={
              member.pto_balance != null && member.pto_balance < 0
                ? "text-danger font-medium"
                : "text-text-primary font-medium"
            }
          >
            {member.pto_balance != null
              ? `${member.pto_balance.toFixed(1)} d`
              : "—"}
          </dd>
        </div>
        <div>
          <dt className="text-text-muted">Pending docs</dt>
          <dd className="flex items-center gap-1 font-medium">
            {pendingDocs > 0 ? (
              <>
                <FileWarning className="h-3.5 w-3.5 text-warning" />
                <span className="text-warning">{pendingDocs}</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                <span className="text-text-primary">0</span>
              </>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-text-muted">Last review</dt>
          <dd className="text-text-primary font-medium">
            {member.last_review_date || "—"}
          </dd>
        </div>
      </dl>

      {member.upcoming_leave && !onLeaveNow && (
        <div className="mt-3 flex items-center gap-2 text-xs text-text-muted">
          <Calendar className="h-3.5 w-3.5" />
          Out {member.upcoming_leave.start_date} →{" "}
          {member.upcoming_leave.end_date}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 text-xs">
        <Link
          to={`/employees/${member.id}`}
          className="text-accent hover:underline"
        >
          Profile
        </Link>
        <span className="text-text-muted">·</span>
        <Link
          to={`/leave?for=${member.id}`}
          className="text-accent hover:underline"
        >
          Request time off
        </Link>
        <span className="text-text-muted">·</span>
        <Link
          to={`/performance?subject=${member.id}`}
          className="text-accent hover:underline"
        >
          Review
        </Link>
      </div>
    </Card>
  );
}
